import React from 'react';
import { cn } from '../../utils/cn';
import { highlightRing } from '../../styles/colors';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  error?: string;
  icon?: React.ReactNode;
  helperText?: string;
  /** Temporary scroll-to highlight (app standard ring). */
  highlighted?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className = '', label, error, icon, helperText, highlighted = false, ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400 text-sm">
                {icon}
              </span>
            </div>
          )}
          <input
            className={cn(
              'block w-full rounded-xl border px-3.5 py-2.5 text-base shadow-sm sm:text-sm',
              'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500',
              'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500',
              error
                ? 'border-red-300 text-red-900 placeholder-red-300'
                : 'border-gray-200 text-gray-900 placeholder-gray-400 hover:border-gray-300 hover:shadow-sm',
              highlighted
                ? `transition-colors duration-500 ${highlightRing.combined}`
                : 'bg-white transition-colors duration-500',
              icon ? 'pl-10' : '',
              className
            )}
            ref={ref}
            {...props}
          />
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
